'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, LogIn, UserPlus } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

const PENDING_SCORE_KEY = 'adversarymatch_pending_score';

type PendingScore = {
  timeSeconds: number;
  moves: number;
  savedAt: number;
};

function getPendingScore(): PendingScore | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PENDING_SCORE_KEY);
    if (!raw) return null;
    const parsed: PendingScore = JSON.parse(raw);
    // Discard if older than 2 hours
    if (Date.now() - parsed.savedAt > 2 * 60 * 60 * 1000) {
      localStorage.removeItem(PENDING_SCORE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

async function savePendingScore(username: string): Promise<void> {
  const pending = getPendingScore();
  if (!pending) return;
  // Small delay to ensure session cookie is set before API call
  await new Promise((resolve) => setTimeout(resolve, 800));
  try {
    const res = await fetch('/api/game/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        player_name: username,
        time_seconds: pending.timeSeconds,
        moves: pending.moves,
      }),
    });
    if (res.ok) {
      localStorage.removeItem(PENDING_SCORE_KEY);
    } else if (res.status === 401) {
      // Session not ready yet — retry once after another delay
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const retry = await fetch('/api/game/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_name: username,
          time_seconds: pending.timeSeconds,
          moves: pending.moves,
        }),
      });
      if (retry.ok) localStorage.removeItem(PENDING_SCORE_KEY);
    } else {
      localStorage.removeItem(PENDING_SCORE_KEY);
    }
  } catch {
    // Silently fail — score save is best-effort
  }
}

type LoginFormData = {
  email: string;
  password: string;
  rememberMe: boolean;
};

type SignupFormData = {
  username: string;
  email: string;
  organization: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
};

// Blocked free email domains
const FREE_EMAIL_DOMAINS = [
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.uk', 'yahoo.co.jp', 'yahoo.fr', 'yahoo.de', 'yahoo.es', 'yahoo.it',
  'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de', 'hotmail.es',
  'outlook.com', 'outlook.co.uk', 'outlook.fr', 'outlook.de',
  'live.com', 'live.co.uk', 'live.fr',
  'msn.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com',
  'protonmail.com', 'proton.me',
  'mail.com', 'email.com',
  'zoho.com',
  'yandex.com', 'yandex.ru',
  'inbox.com',
  'fastmail.com',
  'tutanota.com',
  'gmx.com', 'gmx.net', 'gmx.de',
  'web.de',
  'qq.com', '163.com', '126.com',
];

function isBusinessEmail(email: string): boolean {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return false;
  return !FREE_EMAIL_DOMAINS.includes(domain);
}

type TabType = 'login' | 'signup';

export default function AuthForm() {
  const [tab, setTab] = useState<TabType>('login');
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const router = useRouter();
  const { signIn, signUp } = useAuth();

  const loginForm = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const signupForm = useForm<SignupFormData>({
    defaultValues: { username: '', email: '', organization: '', password: '', confirmPassword: '', agreeTerms: false },
  });

  async function handleLogin(data: LoginFormData) {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await signIn(data.email.trim().toLowerCase(), data.password);
      router.push('/');
      router.refresh();
    } catch (err: unknown) {
      // Generic error message — do not reveal whether email exists
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('Too many') || msg.includes('rate')) {
        setSubmitError('Too many login attempts. Please wait 15 minutes and try again.');
      } else {
        setSubmitError('Invalid email or password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSignup(data: SignupFormData) {
    setIsSubmitting(true);
    setSubmitError('');

    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      setIsSubmitting(false);
      return;
    }

    // Sanitize username and organization — strip HTML/script tags
    const sanitize = (s: string) => s.replace(/<[^>]*>/g, '').replace(/[<>"'`]/g, '').trim().slice(0, 100);
    const safeUsername = sanitize(data.username);
    const safeOrg = sanitize(data.organization);

    if (!safeUsername) {
      signupForm.setError('username', { message: 'Username contains invalid characters' });
      setIsSubmitting(false);
      return;
    }

    try {
      await signUp(data.email.trim().toLowerCase(), data.password, {
        fullName: safeUsername,
        username: safeUsername,
        organization: safeOrg,
      });
      // Save pending guest score if one exists (fire-and-forget; session is now active)
      await savePendingScore(safeUsername);
      router.push('/');
      router.refresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Registration failed. Please try again.';
      setSubmitError(msg.includes('already registered') ? 'An account with this email already exists.' : msg);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="w-full max-w-sm mx-auto">
      {/* Tabs */}
      <div className="flex bg-muted rounded-xl p-1 mb-6">
        {(['login', 'signup'] as TabType[]).map((t) => (
          <button
            key={`tab-${t}`}
            type="button"
            onClick={() => { setTab(t); setSubmitError(''); }}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200
              ${tab === t
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
              }`}
          >
            {t === 'login' ? 'Sign In' : 'Sign Up'}
          </button>
        ))}
      </div>

      {/* Global error */}
      {submitError && (
        <div className="mb-4 p-3 rounded-lg bg-danger/10 border border-danger/30 flex items-start gap-2">
          <span className="text-danger text-[11px] leading-relaxed">{submitError}</span>
        </div>
      )}

      {/* Login form */}
      {tab === 'login' && (
        <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4" noValidate>
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-foreground mb-1.5">
              Email address
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              className={`w-full px-3.5 py-2.5 rounded-lg bg-input border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150
                ${loginForm.formState.errors.email ? 'border-danger' : 'border-border'}`}
              {...loginForm.register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
              })}
            />
            {loginForm.formState.errors.email && (
              <p className="mt-1 text-[11px] text-danger">{loginForm.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="login-password" className="block text-sm font-medium text-foreground mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                id="login-password"
                type={showPass ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter your password"
                className={`w-full px-3.5 py-2.5 pr-10 rounded-lg bg-input border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150
                  ${loginForm.formState.errors.password ? 'border-danger' : 'border-border'}`}
                {...loginForm.register('password', {
                  required: 'Password is required',
                  minLength: { value: 6, message: 'At least 6 characters' },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPass ? 'Hide password' : 'Show password'}
              >
                {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {loginForm.formState.errors.password && (
              <p className="mt-1 text-[11px] text-danger">{loginForm.formState.errors.password.message}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              id="remember-me"
              type="checkbox"
              className="w-4 h-4 rounded border-border bg-input accent-primary"
              {...loginForm.register('rememberMe')}
            />
            <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer">
              Remember me
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
            style={{ minHeight: '42px' }}
          >
            {isSubmitting ? (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <>
                <LogIn size={15} />
                Sign In
              </>
            )}
          </button>
        </form>
      )}

      {/* Signup form */}
      {tab === 'signup' && (
        <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4" noValidate>
          <div>
            <label htmlFor="signup-username" className="block text-sm font-medium text-foreground mb-1.5">
              Username
              <span className="ml-1 text-[10px] text-muted-foreground font-normal">(shown on leaderboard)</span>
            </label>
            <input
              id="signup-username"
              type="text"
              autoComplete="username"
              placeholder="ThreatHunter42"
              className={`w-full px-3.5 py-2.5 rounded-lg bg-input border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150
                ${signupForm.formState.errors.username ? 'border-danger' : 'border-border'}`}
              {...signupForm.register('username', {
                required: 'Username is required',
                minLength: { value: 3, message: 'At least 3 characters' },
              })}
            />
            {signupForm.formState.errors.username && (
              <p className="mt-1 text-[11px] text-danger">{signupForm.formState.errors.username.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="signup-organization" className="block text-sm font-medium text-foreground mb-1.5">
              Organization
            </label>
            <input
              id="signup-organization"
              type="text"
              autoComplete="organization"
              placeholder="Acme Corp"
              className={`w-full px-3.5 py-2.5 rounded-lg bg-input border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150
                ${signupForm.formState.errors.organization ? 'border-danger' : 'border-border'}`}
              {...signupForm.register('organization', {
                required: 'Organization is required',
                minLength: { value: 2, message: 'At least 2 characters' },
              })}
            />
            {signupForm.formState.errors.organization && (
              <p className="mt-1 text-[11px] text-danger">{signupForm.formState.errors.organization.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="signup-email" className="block text-sm font-medium text-foreground mb-1.5">
              Business Email
              <span className="ml-1 text-[10px] text-muted-foreground font-normal">(corporate email required)</span>
            </label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              placeholder="you@yourcompany.com"
              className={`w-full px-3.5 py-2.5 rounded-lg bg-input border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150
                ${signupForm.formState.errors.email ? 'border-danger' : 'border-border'}`}
              {...signupForm.register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                validate: (value) => isBusinessEmail(value) || 'Please use a business/corporate email address (Gmail, Yahoo, Hotmail, etc. are not allowed)',
              })}
            />
            {signupForm.formState.errors.email && (
              <p className="mt-1 text-[11px] text-danger">{signupForm.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="signup-password" className="block text-sm font-medium text-foreground mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                id="signup-password"
                type={showPass ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Min. 6 characters"
                className={`w-full px-3.5 py-2.5 pr-10 rounded-lg bg-input border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150
                  ${signupForm.formState.errors.password ? 'border-danger' : 'border-border'}`}
                {...signupForm.register('password', {
                  required: 'Password is required',
                  minLength: { value: 6, message: 'At least 6 characters' },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPass ? 'Hide password' : 'Show password'}
              >
                {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {signupForm.formState.errors.password && (
              <p className="mt-1 text-[11px] text-danger">{signupForm.formState.errors.password.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="signup-confirm" className="block text-sm font-medium text-foreground mb-1.5">
              Confirm password
            </label>
            <div className="relative">
              <input
                id="signup-confirm"
                type={showConfirmPass ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Repeat your password"
                className={`w-full px-3.5 py-2.5 pr-10 rounded-lg bg-input border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150
                  ${signupForm.formState.errors.confirmPassword ? 'border-danger' : 'border-border'}`}
                {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPass(!showConfirmPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showConfirmPass ? 'Hide password' : 'Show password'}
              >
                {showConfirmPass ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {signupForm.formState.errors.confirmPassword && (
              <p className="mt-1 text-[11px] text-danger">{signupForm.formState.errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="flex items-start gap-2">
            <input
              id="agree-terms"
              type="checkbox"
              className="w-4 h-4 mt-0.5 rounded border-border bg-input accent-primary"
              {...signupForm.register('agreeTerms', { required: 'You must agree to continue' })}
            />
            <label htmlFor="agree-terms" className="text-sm text-muted-foreground cursor-pointer">
              I agree to the terms of service
            </label>
          </div>
          {signupForm.formState.errors.agreeTerms && (
            <p className="text-[11px] text-danger">{signupForm.formState.errors.agreeTerms.message}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
            style={{ minHeight: '42px' }}
          >
            {isSubmitting ? (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <>
                <UserPlus size={15} />
                Create Account
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}